// utils/scbExport.ts
// ⭐ SCB Anywhere file generators — ใช้ร่วมโดย BulkTransferPage (payout ปกติ) + P2pPayoutsPage (export ส่วนค้าง P2P)
// ยอดต่อรายการ = net_amount เสมอ (ทรงเดิมก่อนมี P2P) — ฝั่ง P2P map net_amount = shortfall ก่อนส่งเข้า generator
// ⚠️ ห้ามอ่าน field p2p_* ตรงนี้: payout ปกติจาก /admin/payouts ถูก decorate p2p_shortfall = ยอดเต็มติดมาด้วย
import { bankList } from './bankList';

/** ขั้นต่ำที่ generator ต้องใช้ — Payout เต็ม ๆ ของแต่ละหน้า assign ได้ structurally */
export interface ScbExportPayout {
  bank_name: string;
  bank_account_name: string;
  bank_account_number: string;
  net_amount: number;
}

export function getBankCode(bankName: string): string {
  const lower = bankName.toLowerCase();
  const bank = bankList.find(b =>
    lower.includes(b.shortName.toLowerCase())
  );
  return bank?.bankCode ?? '000';
}

export function sanitizeScbName(name: string): string {
  return name
    .replace(/[^\x20-\x7E\u0E00-\u0E7F]/g, '')
    .replace(/\|/g, '')
    .trim()
    .slice(0, 50);
}

export const txAmt = (p: ScbExportPayout): number => Math.max(0, Number(p.net_amount));

export function generateScbFileAll(payouts: ScbExportPayout[], fromAccount: string): string {
  const scbPayouts = payouts.filter(p => getBankCode(p.bank_name) === '014');
  const otherPayouts = payouts.filter(p => getBankCode(p.bank_name) !== '014');

  const now = new Date();
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');
  const yy = String(now.getFullYear()).slice(2);
  const mm = pad(now.getMonth() + 1);
  const dd = pad(now.getDate());
  const hh = pad(now.getHours());
  const min = pad(now.getMinutes());
  const ss = pad(now.getSeconds());
  const ref12 = `${yy}${mm}${dd}${hh}${min}${ss}`;
  const dateStr = `${now.getFullYear()}${mm}${dd}`;
  const totalAmount = payouts.reduce((s, p) => s + txAmt(p), 0);
  const totalCount = payouts.length;

  const lines: string[] = [];
  lines.push(`HEADER|${ref12}OAT|`);

  if (scbPayouts.length > 0) {
    const scbTotal = scbPayouts.reduce((s, p) => s + txAmt(p), 0);
    lines.push(`BCHDET|${ref12}|OAT|${dateStr}|${fromAccount}|${fromAccount}|${scbTotal.toFixed(2)}|${scbPayouts.length}||`);
    for (const p of scbPayouts) {
      lines.push(`TXNDET||${p.bank_account_number}||014|0111|${txAmt(p).toFixed(2)}||OUR|N||N||${sanitizeScbName(p.bank_account_name)}||||N|||N|N||||N||`);
    }
  }

  if (otherPayouts.length > 0) {
    const otherTotal = otherPayouts.reduce((s, p) => s + txAmt(p), 0);
    lines.push(`BCHDET|${ref12}|RFT|${dateStr}|${fromAccount}|${fromAccount}|${otherTotal.toFixed(2)}|${otherPayouts.length}||`);
    for (const p of otherPayouts) {
      const code = getBankCode(p.bank_name);
      lines.push(`TXNDET||${p.bank_account_number}||${code}|0000|${txAmt(p).toFixed(2)}||OUR|N||N||${sanitizeScbName(p.bank_account_name)}||||N|||N|N||||N||`);
    }
  }

  const batchCount = (scbPayouts.length > 0 ? 1 : 0) + (otherPayouts.length > 0 ? 1 : 0);
  lines.push(`TRAILR|${batchCount}|${totalCount}|${totalAmount.toFixed(2)}`);
  return lines.join('\n');
}

export function generateScbFile(payouts: ScbExportPayout[], fromAccount: string, filterType: 'scb' | 'other'): string {
  const now = new Date();
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');
  const yy = String(now.getFullYear()).slice(2);
  const mm = pad(now.getMonth() + 1);
  const dd = pad(now.getDate());
  const hh = pad(now.getHours());
  const min = pad(now.getMinutes());
  const ss = pad(now.getSeconds());
  const ref12 = `${yy}${mm}${dd}${hh}${min}${ss}`;
  const dateStr = `${now.getFullYear()}${mm}${dd}`;
  const totalAmount = payouts.reduce((s, p) => s + txAmt(p), 0);
  const count = payouts.length;
  // SCB-to-SCB = OAT + subcode 0111 / SCB-to-Other = RFT + subcode 0000
  const serviceType = filterType === 'scb' ? 'OAT' : 'RFT';
  const subCode = filterType === 'scb' ? '0111' : '0000';
  const lines: string[] = [];
  lines.push(`HEADER|${ref12}${serviceType}|`);
  lines.push(`BCHDET|${ref12}|${serviceType}|${dateStr}|${fromAccount}|${fromAccount}|${totalAmount.toFixed(2)}|${count}||`);
  for (const p of payouts) {
    const code = getBankCode(p.bank_name);
    lines.push(`TXNDET||${p.bank_account_number}||${code}|${subCode}|${txAmt(p).toFixed(2)}||OUR|N||N||${sanitizeScbName(p.bank_account_name)}||||N|||N|N||||N||`);
  }
  lines.push(`TRAILR|1|${count}|${totalAmount.toFixed(2)}`);
  return lines.join('\n');
}

export function downloadTextFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
